// src/components/Bank/BankUserDetails.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Table, Badge, Button, Spinner, Alert, Tabs, Tab, Form } from 'react-bootstrap';
import { apiClient } from '../../services/api-client';
import logger from '../../utils/logger';
import { formatCurrency, formatDate } from '../../utils/formatting';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import './BankDashboard.css';


// Account type badge component
const AccountTypeBadge = ({ type }) => {
	const getVariant = () => {
		switch (type?.toLowerCase()) {
			case 'checking': return 'badge-glass text-glass-primary';
			case 'savings': return 'badge-glass text-glass-primary';
			case 'credit': return 'badge-glass text-glass-primary';
			case 'investment': return 'badge-glass text-glass-primary';
			case 'loan': return 'badge-glass text-glass-primary';
			default: return 'badge-glass text-glass-secondary';
		}
	};

	const getGradient = () => {
		switch (type?.toLowerCase()) {
			case 'checking': return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
			case 'savings': return 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)';
			case 'credit': return 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)';
			case 'investment': return 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)';
			case 'loan': return 'linear-gradient(135deg, #fdbb2d 0%, #22c1c3 100%)';
			default: return 'linear-gradient(135deg, #a8a8a8 0%, #d3d3d3 100%)';
		}
	};

	return (
		<span
			className={getVariant()}
			style={{
				background: getGradient(),
				color: 'white',
				padding: '0.3rem 0.8rem',
				borderRadius: '8px',
				fontSize: '0.75rem',
				fontWeight: '600',
				border: 'none',
				textTransform: 'uppercase',
				letterSpacing: '0.5px'
			}}
		>
			{type || 'Other'}
		</span>
	);
};

// Colors for charts
const COLORS = ['#667eea', '#4facfe', '#f093fb', '#fdbb2d', '#ff9a9e', '#00f2fe', '#22c1c3', '#fecfef'];

const BankUserDetails = () => {
	const { bankUserId } = useParams();
	const navigate = useNavigate();

	// State
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [bankUser, setBankUser] = useState(null);
	const [accounts, setAccounts] = useState([]);
	const [transactions, setTransactions] = useState([]);
	const [transactionSummary, setTransactionSummary] = useState(null);
	const [categories, setCategories] = useState([]);
	const [activeTab, setActiveTab] = useState('overview');
	const [transactionFilters, setTransactionFilters] = useState({
		startDate: '',
		endDate: '',
		accountId: '',
		type: '',
		search: ''
	});
	const [transactionPagination, setTransactionPagination] = useState({
		page: 1,
		limit: 50,
		total: 0,
		pages: 1
	});

	// Fetch user details
	useEffect(() => {
		const fetchUserDetails = async () => {
			try {
				setLoading(true);
				setError(null);

				// Get user details
				const response = await apiClient.get(`/bank-client/users/${bankUserId}/details`);
				const { data } = response.data;

				setBankUser(data.bankUser);
				setAccounts(data.accounts);
				setTransactionSummary(data.transactionSummary);
				setCategories(data.categories);

				// Initial transactions
				setTransactions(data.recentTransactions);

			} catch (err) {
				logger.error(`Error fetching bank user details: ${err.message}`);
				setError(`Failed to load bank user details. ${err.response?.data?.message || err.message}`);
			} finally {
				setLoading(false);
			}
		};

		if (bankUserId) {
			fetchUserDetails();
		}
	}, [bankUserId]);

	// Fetch transactions when filters change
	const fetchTransactions = async (page = 1) => {
		try {
			setLoading(true);

			let url = `/bank-client/users/${bankUserId}/transactions?page=${page}&limit=${transactionPagination.limit}`;

			// Add filters to URL
			if (transactionFilters.startDate) {
				url += `&startDate=${transactionFilters.startDate}`;
			}
			if (transactionFilters.endDate) {
				url += `&endDate=${transactionFilters.endDate}`;
			}
			if (transactionFilters.accountId) {
				url += `&accountId=${transactionFilters.accountId}`;
			}
			if (transactionFilters.type) {
				url += `&type=${transactionFilters.type}`;
			}
			if (transactionFilters.search) {
				url += `&search=${encodeURIComponent(transactionFilters.search)}`;
			}

			const response = await apiClient.get(url);
			const { data, pagination } = response.data;

			setTransactions(data);
			setTransactionPagination(pagination);
		} catch (err) {
			logger.error(`Error fetching transactions: ${err.message}`);
			setError(`Failed to load transactions. ${err.response?.data?.message || err.message}`);
		} finally {
			setLoading(false);
		}
	};

	// Apply transaction filters
	const handleApplyFilters = (e) => {
		e.preventDefault();
		fetchTransactions(1); // Reset to first page when applying filters
	};

	// Reset transaction filters
	const handleResetFilters = () => {
		setTransactionFilters({
			startDate: '',
			endDate: '',
			accountId: '',
			type: '',
			search: ''
		});

		// Wait for state update to complete
		setTimeout(() => {
			fetchTransactions(1);
		}, 0);
	};

	// Handle page change
	const handlePageChange = (newPage) => {
		if (newPage < 1 || newPage > transactionPagination.pages) return;
		fetchTransactions(newPage);
	};

	// Prepare category data for pie chart
	const prepareCategoryData = () => {
		if (!categories || categories.length === 0) return [];

		// Get top categories by count
		const topCategories = [...categories]
			.filter(cat => cat.category) // Filter out null categories
			.sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount)) // Sort by absolute amount
			.slice(0, 7); // Get top 7

		// Calculate total for "Others" category if needed
		if (categories.length > 7) {
			const othersSum = categories
				.filter(cat => cat.category && !topCategories.find(tc => tc.category === cat.category))
				.reduce((sum, cat) => sum + parseFloat(cat.amount), 0);

			if (othersSum !== 0) {
				topCategories.push({
					category: 'Others',
					count: categories.length - topCategories.length,
					amount: othersSum
				});
			}
		}

		// Format data for chart
		return topCategories.map(cat => ({
			name: cat.category,
			value: Math.abs(parseFloat(cat.amount))
		}));
	};

	// Render overview tab
	const renderOverview = () => {
		if (!bankUser || !transactionSummary) {
			return (
				<div className="alert-glass alert-glass-info p-4" style={{ borderRadius: '16px' }}>
					<div className="d-flex align-items-center">
						<div className="spinner-glass me-3" style={{ width: '24px', height: '24px' }}></div>
						Loading user information...
					</div>
				</div>
			);
		}

		const categoryData = prepareCategoryData();

		return (
			<>
				<Row>
					<Col md={6}>
						<div className="glass-card mb-4">
							<div className="glass-header">
								<h5 className="mb-0 text-glass-primary">Account Summary</h5>
							</div>
							<div className="p-4">
								<div className="d-flex justify-content-between mb-4">
									<div className="stats-card flex-fill me-3">
										<h6 className="text-glass-muted mb-2">Total Accounts</h6>
										<h3 className="mb-0" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
											{accounts.length}
										</h3>
									</div>
									<div className="stats-card flex-fill ms-3">
										<h6 className="text-glass-muted mb-2">Total Balance</h6>
										<h3 className="mb-0" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
											{formatCurrency(accounts.reduce((sum, acc) => sum + parseFloat(acc.balance), 0), 'USD')}
										</h3>
									</div>
								</div>

								{accounts.length > 0 ? (
									<div className="table-responsive">
										<table className="table table-glass">
											<thead>
												<tr>
													<th className="border-0 bg-transparent text-glass-primary">Account</th>
													<th className="border-0 bg-transparent text-glass-primary">Type</th>
													<th className="border-0 bg-transparent text-glass-primary text-end">Balance</th>
												</tr>
											</thead>
											<tbody>
												{accounts.map(account => (
													<tr key={account.id}>
														<td className="border-0 bg-transparent text-glass-primary">{account.name}</td>
														<td className="border-0 bg-transparent">
															<AccountTypeBadge type={account.type} />
														</td>
														<td className="border-0 bg-transparent text-glass-primary text-end font-weight-bold">
															{formatCurrency(account.balance, account.currency)}
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								) : (
									<div className="alert-glass alert-glass-info p-3">
										No accounts found for this user.
									</div>
								)}
							</div>
						</div>
					</Col>

					<Col md={6}>
						<div className="glass-card mb-4">
							<div className="glass-header">
								<h5 className="mb-0 text-glass-primary">Transaction Summary</h5>
							</div>
							<div className="p-4">
								<Row>
									<Col xs={6}>
										<div className="text-center mb-3 stats-card">
											<h6 className="text-glass-muted mb-2">Total Transactions</h6>
											<h3 className="mb-1" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
												{transactionSummary.total}
											</h3>
											{transactionSummary.pending > 0 && (
												<small style={{ color: '#fdbb2d' }}>
													{transactionSummary.pending} pending
												</small>
											)}
										</div>
									</Col>
									<Col xs={6}>
										<div className="text-center mb-3 stats-card">
											<h6 className="text-glass-muted mb-2">Net Balance</h6>
											<h3 className={`mb-1 ${parseFloat(transactionSummary.totalIncome) + parseFloat(transactionSummary.totalExpense) >= 0 ? 'text-success' : 'text-danger'}`}>
												{formatCurrency(parseFloat(transactionSummary.totalIncome) + parseFloat(transactionSummary.totalExpense), 'USD')}
											</h3>
										</div>
									</Col>
								</Row>

								<Row>
									<Col xs={6}>
										<div className="text-center stats-card">
											<div className="text-success">
												<h6 className="text-glass-muted mb-2">Income</h6>
												<h4 className="text-success mb-0">
													{formatCurrency(parseFloat(transactionSummary.totalIncome), 'USD')}
												</h4>
											</div>
										</div>
									</Col>
									<Col xs={6}>
										<div className="text-center stats-card">
											<div className="text-danger">
												<h6 className="text-glass-muted mb-2">Expenses</h6>
												<h4 className="text-danger mb-0">
													{formatCurrency(parseFloat(transactionSummary.totalExpense), 'USD')}
												</h4>
											</div>
										</div>
									</Col>
								</Row>
							</div>
						</div>
					</Col>
				</Row>

				<Row>
					<Col md={6}>
						<div className="glass-card mb-4">
							<div className="glass-header">
								<h5 className="mb-0 text-glass-primary">Spending by Category</h5>
							</div>
							<div className="p-4">
								{categoryData.length > 0 ? (
									<div className="chart-container-glass">
										<ResponsiveContainer width="100%" height={300}>
											<PieChart>
												<Pie
													data={categoryData}
													dataKey="value"
													nameKey="name"
													cx="50%"
													cy="50%"
													outerRadius={100}
													fill="#8884d8"
													label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
												>
													{categoryData.map((entry, index) => (
														<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
													))}
												</Pie>
												<Tooltip formatter={(value) => formatCurrency(value, 'USD')} />
												<Legend />
											</PieChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center p-4">
										<p className="text-glass-muted">No category data available</p>
									</div>
								)}
							</div>
						</div>
					</Col>

					<Col md={6}>
						<div className="glass-card mb-4">
							<div className="glass-header">
								<h5 className="mb-0 text-glass-primary">Recent Transactions</h5>
							</div>
							<div className="p-4">
								{transactions.length > 0 ? (
									<div className="glass-scrollbar" style={{ maxHeight: '300px', overflowY: 'auto' }}>
										<table className="table table-glass">
											<thead>
												<tr>
													<th className="border-0 bg-transparent text-glass-primary">Date</th>
													<th className="border-0 bg-transparent text-glass-primary">Description</th>
													<th className="border-0 bg-transparent text-glass-primary text-end">Amount</th>
												</tr>
											</thead>
											<tbody>
												{transactions.slice(0, 10).map(transaction => (
													<tr key={transaction.id}>
														<td className="border-0 bg-transparent text-glass-primary">
															{formatDate(transaction.date)}
														</td>
														<td className="border-0 bg-transparent text-glass-primary text-truncate" style={{ maxWidth: '200px' }}>
															{transaction.description}
														</td>
														<td className={`border-0 bg-transparent text-end font-weight-bold ${parseFloat(transaction.amount) >= 0 ? 'text-success' : 'text-danger'}`}>
															{formatCurrency(transaction.amount, 'USD')}
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								) : (
									<div className="alert-glass alert-glass-info p-3">
										No recent transactions found.
									</div>
								)}

								<div className="text-center mt-3">
									<button
										className="btn btn-glass-primary"
										onClick={() => setActiveTab('transactions')}
									>
										View All Transactions
									</button>
								</div>
							</div>
						</div>
					</Col>
				</Row>
			</>
		);
	};

	// Render accounts tab
	const renderAccounts = () => {
		return (
			<div className="glass-card mb-4">
				<div className="glass-header">
					<h5 className="mb-0 text-glass-primary">User Accounts</h5>
				</div>
				<div className="p-4">
					{accounts.length > 0 ? (
						<div className="table-responsive">
							<table className="table table-glass">
								<thead>
									<tr>
										<th className="border-0 bg-transparent text-glass-primary">Account ID</th>
										<th className="border-0 bg-transparent text-glass-primary">Name</th>
										<th className="border-0 bg-transparent text-glass-primary">Type</th>
										<th className="border-0 bg-transparent text-glass-primary">Balance</th>
										<th className="border-0 bg-transparent text-glass-primary">Available</th>
										<th className="border-0 bg-transparent text-glass-primary">Currency</th>
										<th className="border-0 bg-transparent text-glass-primary">Last Updated</th>
									</tr>
								</thead>
								<tbody>
									{accounts.map(account => (
										<tr key={account.id}>
											<td className="border-0 bg-transparent text-glass-primary text-truncate" style={{ maxWidth: '150px' }}>
												{account.accountId}
											</td>
											<td className="border-0 bg-transparent text-glass-primary font-weight-bold">
												{account.name}
											</td>
											<td className="border-0 bg-transparent">
												<AccountTypeBadge type={account.type} />
												{account.subtype && <small className="ms-2 text-glass-muted">({account.subtype})</small>}
											</td>
											<td className="border-0 bg-transparent text-glass-primary font-weight-bold">
												{formatCurrency(account.balance, account.currency)}
											</td>
											<td className="border-0 bg-transparent text-glass-primary">
												{formatCurrency(account.availableBalance, account.currency)}
											</td>
											<td className="border-0 bg-transparent text-glass-primary">{account.currency}</td>
											<td className="border-0 bg-transparent text-glass-primary">
												{formatDate(account.lastUpdated)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					) : (
						<div className="alert-glass alert-glass-info p-3">
							No accounts found for this user.
						</div>
					)}
				</div>
			</div>
		);
	};

	// Render transactions tab
	const renderTransactions = () => {
		return (
			<>
				<div className="glass-card mb-4">
					<div className="glass-header">
						<div className="d-flex justify-content-between align-items-center">
							<h5 className="mb-0 text-glass-primary">Transaction Filters</h5>
						</div>
					</div>
					<div className="p-4">
						<Form onSubmit={handleApplyFilters}>
							<Row>
								<Col md={2}>
									<Form.Group className="mb-3">
										<Form.Label className="text-glass-primary font-weight-bold">Start Date</Form.Label>
										<Form.Control
											className="form-control-glass"
											type="date"
											value={transactionFilters.startDate}
											onChange={(e) => setTransactionFilters({ ...transactionFilters, startDate: e.target.value })}
										/>
									</Form.Group>
								</Col>
								<Col md={2}>
									<Form.Group className="mb-3">
										<Form.Label className="text-glass-primary font-weight-bold">End Date</Form.Label>
										<Form.Control
											className="form-control-glass"
											type="date"
											value={transactionFilters.endDate}
											onChange={(e) => setTransactionFilters({ ...transactionFilters, endDate: e.target.value })}
										/>
									</Form.Group>
								</Col>
								<Col md={3}>
									<Form.Group className="mb-3">
										<Form.Label className="text-glass-primary font-weight-bold">Account</Form.Label>
										<Form.Select
											className="form-select-glass"
											value={transactionFilters.accountId}
											onChange={(e) => setTransactionFilters({ ...transactionFilters, accountId: e.target.value })}
										>
											<option value="">All Accounts</option>
											{accounts.map(account => (
												<option key={account.accountId} value={account.accountId}>
													{account.name}
												</option>
											))}
										</Form.Select>
									</Form.Group>
								</Col>
								<Col md={2}>
									<Form.Group className="mb-3">
										<Form.Label className="text-glass-primary font-weight-bold">Type</Form.Label>
										<Form.Select
											className="form-select-glass"
											value={transactionFilters.type}
											onChange={(e) => setTransactionFilters({ ...transactionFilters, type: e.target.value })}
										>
											<option value="">All Types</option>
											<option value="income">Income</option>
											<option value="expense">Expense</option>
											<option value="transfer">Transfer</option>
										</Form.Select>
									</Form.Group>
								</Col>
								<Col md={3}>
									<Form.Group className="mb-3">
										<Form.Label className="text-glass-primary font-weight-bold">Search</Form.Label>
										<Form.Control
											className="form-control-glass"
											type="text"
											placeholder="Search description or merchant"
											value={transactionFilters.search}
											onChange={(e) => setTransactionFilters({ ...transactionFilters, search: e.target.value })}
										/>
									</Form.Group>
								</Col>
							</Row>
							<div className="d-flex gap-2">
								<button type="submit" className="btn btn-glass-primary">
									Apply Filters
								</button>
								<button type="button" className="btn btn-glass" onClick={handleResetFilters}>
									Reset
								</button>
							</div>
						</Form>
					</div>
				</div>

				<div className="glass-card mb-4">
					<div className="glass-header">
						<h5 className="mb-0 text-glass-primary">Transaction List</h5>
					</div>
					<div className="p-4">
						{loading ? (
							<div className="text-center p-4">
								<div className="spinner-glass mx-auto mb-3" style={{ width: '40px', height: '40px' }}></div>
								<p className="text-glass-primary">Loading transactions...</p>
							</div>
						) : transactions.length > 0 ? (
							<>
								<div className="table-responsive">
									<table className="table table-glass">
										<thead>
											<tr>
												<th className="border-0 bg-transparent text-glass-primary">Date</th>
												<th className="border-0 bg-transparent text-glass-primary">Description</th>
												<th className="border-0 bg-transparent text-glass-primary">Amount</th>
												<th className="border-0 bg-transparent text-glass-primary">Account</th>
												<th className="border-0 bg-transparent text-glass-primary">Category</th>
												<th className="border-0 bg-transparent text-glass-primary">Merchant</th>
												<th className="border-0 bg-transparent text-glass-primary">Status</th>
											</tr>
										</thead>
										<tbody>
											{transactions.map(transaction => {
												const account = accounts.find(a => a.accountId === transaction.accountId);
												return (
													<tr key={transaction.id}>
														<td className="border-0 bg-transparent text-glass-primary">
															{formatDate(transaction.date)}
														</td>
														<td className="border-0 bg-transparent text-glass-primary text-truncate" style={{ maxWidth: '200px' }}>
															{transaction.description}
														</td>
														<td className={`border-0 bg-transparent font-weight-bold ${parseFloat(transaction.amount) >= 0 ? 'text-success' : 'text-danger'}`}>
															{formatCurrency(transaction.amount, account?.currency || 'USD')}
														</td>
														<td className="border-0 bg-transparent text-glass-primary">
															{account?.name || transaction.accountId}
														</td>
														<td className="border-0 bg-transparent text-glass-primary">
															{transaction.category || 'Uncategorized'}
														</td>
														<td className="border-0 bg-transparent text-glass-primary">
															{transaction.merchantName || 'N/A'}
														</td>
														<td className="border-0 bg-transparent">
															{transaction.pending ? (
																<span className="badge-glass" style={{ background: 'linear-gradient(135deg, #fdbb2d 0%, #22c1c3 100%)', color: 'white', padding: '0.3rem 0.6rem', borderRadius: '6px', fontSize: '0.7rem' }}>
																	Pending
																</span>
															) : (
																<span className="badge-glass" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white', padding: '0.3rem 0.6rem', borderRadius: '6px', fontSize: '0.7rem' }}>
																	Cleared
																</span>
															)}
														</td>
													</tr>
												);
											})}
										</tbody>
									</table>
								</div>

								{/* Pagination */}
								{transactionPagination.pages > 1 && (
									<div className="d-flex justify-content-center mt-4">
										<button
											className="btn btn-glass me-2"
											onClick={() => handlePageChange(transactionPagination.page - 1)}
											disabled={transactionPagination.page === 1}
										>
											Previous
										</button>

										<span className="mx-3 d-flex align-items-center text-glass-primary font-weight-bold">
											Page {transactionPagination.page} of {transactionPagination.pages}
										</span>

										<button
											className="btn btn-glass ms-2"
											onClick={() => handlePageChange(transactionPagination.page + 1)}
											disabled={transactionPagination.page === transactionPagination.pages}
										>
											Next
										</button>
									</div>
								)}
							</>
						) : (
							<div className="alert-glass alert-glass-info p-3">
								No transactions found matching the current filters.
							</div>
						)}
					</div>
				</div>
			</>
		);
	};

	return (
		<div className="container-fluid py-4" style={{ minHeight: '100vh' }}>
			{loading && !bankUser ? (
				<div className="text-center p-5">
					<div className="spinner-glass mx-auto mb-3" style={{ width: '60px', height: '60px' }}></div>
					<p className="text-glass-light mt-3 h5">Loading user details...</p>
				</div>
			) : error ? (
				<div className="alert-glass alert-glass-danger p-4" style={{ borderRadius: '16px' }}>
					{error}
				</div>
			) : bankUser ? (
				<>
					<div className="d-flex justify-content-between align-items-center mb-4">
						<div className="glass-card p-4 flex-grow-1 me-3">
							<h1 className="mb-2 text-glass-light">{bankUser.name || 'Bank User'}</h1>
							<p className="text-glass-light opacity-75 mb-0">ID: {bankUser.bankUserId}</p>
						</div>
						<button
							className="btn btn-glass floating"
							onClick={() => navigate('/bank-dashboard')}
						>
							← Back to Dashboard
						</button>
					</div>

					<div className="nav-tabs-glass mb-4">
						<ul className="nav nav-tabs border-0">
							<li className="nav-item">
								<button
									className={`nav-link border-0 ${activeTab === 'overview' ? 'active' : ''}`}
									onClick={() => setActiveTab('overview')}
								>
									Overview
								</button>
							</li>
							<li className="nav-item">
								<button
									className={`nav-link border-0 ${activeTab === 'accounts' ? 'active' : ''}`}
									onClick={() => setActiveTab('accounts')}
								>
									Accounts
								</button>
							</li>
							<li className="nav-item">
								<button
									className={`nav-link border-0 ${activeTab === 'transactions' ? 'active' : ''}`}
									onClick={() => setActiveTab('transactions')}
								>
									Transactions
								</button>
							</li>
						</ul>
					</div>

					{activeTab === 'overview' && renderOverview()}
					{activeTab === 'accounts' && renderAccounts()}
					{activeTab === 'transactions' && renderTransactions()}
				</>
			) : (
				<div className="alert-glass alert-glass-warning p-4" style={{ borderRadius: '16px' }}>
					User not found
				</div>
			)}
		</div>
	);
};

export default BankUserDetails;